import { useEffect, useMemo, useState } from "react";

const resizeObserver = new ResizeObserver((entries) => {
  for (const entry of entries) {
    const callbacks = subscribers.get(entry.target);
    if (!callbacks) continue;
    for (const callback of callbacks.values()) {
      callback(entry);
    }
  }
});

type Callback = (entry: ResizeObserverEntry) => void;

const subscribers = new Map<Element, Map<symbol, Callback>>();

const subscribe = (element: Element, callback: Callback) => {
  const symbol = Symbol();

  let callbacks = subscribers.get(element);
  if (!callbacks) {
    callbacks = new Map();
    subscribers.set(element, callbacks);
  }

  callbacks.set(symbol, callback);

  if (callbacks.size === 1) {
    resizeObserver.observe(element, { box: "border-box" });
  }

  return symbol;
};

const unsubscribe = (element: Element, symbol: symbol) => {
  const callbacks = subscribers.get(element);
  if (!callbacks?.has(symbol)) return;

  callbacks.delete(symbol);

  if (callbacks.size === 0) {
    subscribers.delete(element);
    resizeObserver.unobserve(element);
  }
};

export function useElementSize(element?: Element | null) {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!element) {
      setWidth(0);
      setHeight(0);
      return;
    }

    const symbol = subscribe(element, ({ borderBoxSize: [size = null] }) => {
      setWidth(size?.inlineSize ?? 0);
      setHeight(size?.blockSize ?? 0);
    });

    return () => unsubscribe(element, symbol);
  }, [element]);

  return useMemo<[width: number, height: number]>(
    () => [width, height],
    [width, height],
  );
}

export const useViewportSize = () => useElementSize(document.documentElement);
